/**
 * Place search (spec 13) — debounced, abort-on-supersede fetch against the
 * API proxy (never Kartverket direct). Debounce/abort state lives in module
 * closures (same shape as followMetrics.ts's `unsub`), not store state,
 * since it's implementation plumbing rather than something a component reads.
 */
import { create } from 'zustand';
import { searchResponse, type SearchHit } from '@boatnav/schemas';
import { listSearchRecents, pushSearchRecent, type SearchRecent } from '../../lib/db.js';

export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MIN_LEN = 2;

interface SearchState {
  query: string;
  results: SearchHit[];
  recents: SearchRecent[];
  loading: boolean;
  /** True once a fetch for the current query has settled (hit or miss) —
   *  lets the sheet distinguish "haven't searched yet" from "no results". */
  searched: boolean;
  setQuery: (q: string) => void;
  loadRecents: () => void;
  selectHit: (hit: SearchHit) => Promise<void>;
  reset: () => void;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: AbortController | null = null;

function cancelPending(): void {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  inFlight?.abort();
  inFlight = null;
}

async function runSearch(q: string): Promise<void> {
  const controller = new AbortController();
  inFlight = controller;
  useSearchStore.setState({ loading: true });

  try {
    const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, { signal: controller.signal });
    if (!res.ok) {
      if (!controller.signal.aborted) useSearchStore.setState({ results: [], loading: false, searched: true });
      return;
    }
    const data = searchResponse.parse(await res.json());
    // A response that lost the abort race but resolved anyway must not
    // clobber a newer, still-pending query's eventual result.
    if (controller.signal.aborted) return;
    useSearchStore.setState({ results: data.hits, loading: false, searched: true });
  } catch {
    // Includes AbortError (superseded query) and offline — either way the
    // sheet shows the same honest empty state, never an error dialog.
    if (!controller.signal.aborted) useSearchStore.setState({ results: [], loading: false, searched: true });
  } finally {
    if (inFlight === controller) inFlight = null;
  }
}

export const useSearchStore = create<SearchState>((set) => ({
  query: '',
  results: [],
  recents: [],
  loading: false,
  searched: false,

  setQuery: (query) => {
    set({ query });
    cancelPending();

    const trimmed = query.trim();
    if (trimmed.length < SEARCH_MIN_LEN) {
      set({ results: [], loading: false, searched: false });
      return;
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void runSearch(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  },

  loadRecents: () => {
    void listSearchRecents().then((recents) => useSearchStore.setState({ recents }));
  },

  selectHit: async (hit) => {
    cancelPending();
    set({ query: '', results: [], loading: false, searched: false });
    await pushSearchRecent(hit).catch(() => undefined);
  },

  reset: () => {
    cancelPending();
    set({ query: '', results: [], loading: false, searched: false });
  },
}));
